// Headers match the price sheet's columns, so the grid reads like the spreadsheet admins
// maintained before.
// Widths fit each header alongside its sort and menu icons.
export const PRICE_FIELDS = [
  { field: 'legendFee', headerName: 'Legend_Fee', width: 150 },
  { field: 'multiVialFee', headerName: 'Multi_Vial_Fee', width: 170 },
  { field: 'batchFee', headerName: 'Batch_Fee', width: 140 },
  { field: 'suppliesFee', headerName: 'Supplies_Fee', width: 160 },
  { field: 'unitPrice', headerName: 'Unit_Price', width: 150, required: true },
];

// Unit prices go down to fractions of a cent (e.g. $0.045), so allow up to 4 decimals.
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

export const formatPrice = (value) =>
  value === null || value === undefined ? '' : currencyFormatter.format(value);

// Pricing NDCs are 11 digits with no separators; inventory may store them hyphenated.
export const normalizeNdc = (ndc) => (ndc ?? '').replace(/[-\s]/g, '');

// Every descriptive column is required; of the prices, only Unit_Price is (see PRICE_FIELDS).
export const REQUIRED_TEXT_FIELDS = [
  { field: 'name', headerName: 'Name' },
  { field: 'strength', headerName: 'Strength' },
  { field: 'dgDrugName', headerName: 'DG_Drug_Name' },
  { field: 'code', headerName: 'Code' },
  { field: 'coldShipped', headerName: 'Cold_Shipped' },
];

// AG Grid valueParsers for the editable columns.
// Text columns are trimmed; a blank one is stored as empty. They're all required, but that is
// shown on the grid rather than refused here: the page turns an empty required cell red and holds
// Save until it's filled (see isRequiredMissing).
export const parseText = ({ newValue }) => newValue?.trim() || null;

// The fields a save sends, in the API's names; everything else on a grid row is dropped.
const SAVE_FIELDS = [
  'ndc',
  'name',
  'strength',
  'dgDrugName',
  'code',
  'coldShipped',
  ...PRICE_FIELDS.map(({ field }) => field),
];

// A full row as the API expects it: PUT replaces every field, so a fee left out would be cleared.
export const toSavePayload = (row) =>
  Object.fromEntries(SAVE_FIELDS.map((field) => [field, row[field] ?? null]));

// Folds the rows a save returned (as stored) into a list: existing NDCs are replaced in place,
// new ones appended. Rows the save didn't touch are kept as they are.
export const mergeSavedRows = (list, saved) => {
  const savedByNdc = new Map(saved.map((row) => [row.ndc, row]));
  const merged = list.map((row) => savedByNdc.get(row.ndc) ?? row);
  const existing = new Set(list.map(({ ndc }) => ndc));
  return [...merged, ...saved.filter(({ ndc }) => !existing.has(ndc))];
};

export const saveErrorKey = (ndc, field) => `${ndc}:${field}`;

/**
 * Turns a failed save's per-row `details` (from core or the Invoicing API, same shape) into a Map
 * of cell -> message. `index` is the row's position in what was sent, so the NDC is taken from
 * the payload; a row-level problem (no `field`, e.g. a duplicate NDC) is put on the NDC cell.
 */
export const toSaveErrors = (details, payload) =>
  new Map(
    details.map(({ index, ndc, field, message }) => [
      saveErrorKey(payload[index]?.ndc ?? ndc, field ?? 'ndc'),
      message,
    ])
  );

// The columns a row can't be saved without; the four fees are optional.
export const REQUIRED_FIELDS = [
  'name',
  'strength',
  'dgDrugName',
  'code',
  'coldShipped',
  'unitPrice',
];

export const isRequiredMissing = (row, field) => {
  const value = row[field];
  return value === null || value === undefined || (typeof value === 'string' && !value.trim());
};

const PRICE_FIELD_NAMES = new Set(PRICE_FIELDS.map(({ field }) => field));

// Every field the grid checks before a save.
export const VALIDATED_FIELDS = [...new Set([...REQUIRED_FIELDS, ...PRICE_FIELD_NAMES])];

/**
 * What's wrong with one cell, or null. The grid accepts any entry; a cell with a problem is shown
 * red and holds Save until it's fixed, rather than being refused as it's typed.
 */
export const getCellProblem = (row, field) => {
  if (REQUIRED_FIELDS.includes(field) && isRequiredMissing(row, field)) return 'is required';
  if (PRICE_FIELD_NAMES.has(field) && typeof row[field] === 'number' && row[field] < 0) {
    return 'must be 0 or more';
  }
  return null;
};
